import { useCallback, useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { DashSidebar } from "@/components/DashSidebar";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Briefcase, MapPin, ArrowsClockwise, Wallet } from "@phosphor-icons/react";
import { toast } from "sonner";
import UpgradeGate from "@/components/UpgradeGate";
import { hasPlan } from "@/lib/planAccess";

export default function Jobs() {
  const { user } = useAuth();
  const paid = hasPlan(user, "pro");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cacheInfo, setCacheInfo] = useState(null);

  const load = useCallback(async (forceRefresh = false) => {
    if (!user?.onboarded) { setLoading(false); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/match${forceRefresh ? "?refresh=true" : ""}`);
      setJobs(data.jobs || []);
      setCacheInfo({ cached: !!data.cached, generatedAt: data.generated_at, nextUpdateAt: data.next_update_at });
    } catch (err) {
      if (err.response?.status !== 400) {
        console.error("jobs load failed", err);
        toast.error("Failed to load jobs");
      }
    }
    finally { setLoading(false); }
  }, [user?.onboarded]);
  useEffect(() => { load(false); }, [load]);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="jobs-page">
      <Navbar />
      <DashSidebar />
      <main className="lg:ml-60 pt-24 px-4 sm:px-6 md:px-10 pb-24 max-w-6xl mx-auto lg:mx-0">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6 sm:mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-blue-500 font-semibold mb-2">// Smart Matching</div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl tracking-tight font-medium">Job Matches</h1>
            <p className="text-zinc-500 mt-2 text-sm sm:text-base">AI-matched plausible openings based on your profile. Free includes 2 matches; Pro and Premium include 6 with weekly refresh.</p>
          </div>
          {paid ? (
            <button onClick={() => load(true)} data-testid="refresh-jobs" className="self-start sm:self-auto bg-white/5 hover:bg-white/10 border border-white/10 px-4 py-2 rounded-lg text-sm inline-flex items-center gap-2">
              <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh weekly
            </button>
          ) : (
            <button onClick={() => window.location.assign("/pricing")} className="self-start sm:self-auto bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">Unlock 6 jobs + weekly refresh</button>
          )}
        </div>
        {!loading && cacheInfo?.generatedAt && (
          <div className="mb-5 text-xs text-zinc-500">
            {cacheInfo.cached ? "✓ Showing your saved 7-day job snapshot" : "✓ New job snapshot generated"}
            {cacheInfo.nextUpdateAt && ` · Next automatic update: ${new Date(cacheInfo.nextUpdateAt).toLocaleDateString()}`}
          </div>
        )}

        {loading && <div className="text-zinc-500">Finding matches...</div>}
        {!loading && jobs.length === 0 && <div className="text-zinc-500">No matches yet. Try refreshing.</div>}

        <div className="grid md:grid-cols-2 gap-4" data-testid="jobs-grid">
          {jobs.map((j, i) => (
            <div key={`${i}-${j.title}-${j.company}`} className="bg-[#0F0F12] border border-white/10 rounded-xl p-4 sm:p-6 hover:border-blue-500/30 transition-colors" data-testid={`job-card-${i}`}>
              <div className="flex items-start justify-between gap-3 sm:gap-4">
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-medium break-words">{j.title}</h3>
                  <div className="text-xs sm:text-sm text-zinc-400 flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                    <span className="flex items-center gap-1 min-w-0"><Briefcase size={12} className="shrink-0" /> <span className="truncate">{j.company}</span></span>
                    <span className="flex items-center gap-1 min-w-0"><MapPin size={12} className="shrink-0" /> <span className="truncate">{j.location}</span></span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Match</div>
                  <div className={`text-xl sm:text-2xl font-medium font-mono ${j.match_score >= 80 ? "text-emerald-400" : j.match_score >= 60 ? "text-blue-400" : "text-zinc-400"}`}>{j.match_score}%</div>
                </div>
              </div>
              <p className="text-sm text-zinc-400 mt-3 leading-relaxed">{j.description}</p>
              {j.why_recommended && (
                <div className="mt-3 bg-blue-500/5 border border-blue-500/20 rounded-md px-3 py-2 text-xs text-blue-200" data-testid={`job-why-${i}`}>
                  <span className="font-medium text-blue-400">Why this fits:</span> {j.why_recommended}
                </div>
              )}
              <div className="mt-4">
                <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Skills required</div>
                <div className="flex flex-wrap gap-2">
                  {(j.skills_required || []).slice(0, 6).map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-blue-500/10 text-blue-300 border border-blue-500/20">{s}</span>
                  ))}
                </div>
              </div>
              {!!(j.missing_skills || []).length && (
                <div className="mt-3">
                  <div className="text-[10px] tracking-widest uppercase text-zinc-500 mb-1.5">Skills you&apos;ll need to build</div>
                  <div className="flex flex-wrap gap-2">
                  {(j.missing_skills || []).slice(0, 6).map((s) => (
                      <span key={s} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded bg-amber-500/10 text-amber-300 border border-amber-500/20">{s}</span>
                    ))}
                  </div>
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-white/5 flex items-center gap-2 text-sm">
                <Wallet size={14} className="text-emerald-400" />
                <span className="font-mono text-emerald-400">{j.currency === "AED" ? "AED " : j.currency === "INR" ? "₹" : j.currency === "GBP" ? "£" : j.currency === "EUR" ? "€" : j.currency === "CAD" ? "C$" : j.currency === "AUD" ? "A$" : j.currency === "NZD" ? "NZ$" : j.currency === "JPY" ? "¥" : j.currency === "SAR" ? "SAR " : j.currency === "QAR" ? "QAR " : j.currency === "KWD" ? "KWD " : j.currency === "BHD" ? "BHD " : j.currency === "OMR" ? "OMR " : j.currency === "ZAR" ? "R" : j.currency === "MYR" ? "RM" : j.currency === "SGD" ? "S$" : "$"}{(j.salary_min || 0).toLocaleString()} – {j.currency === "AED" ? "AED " : j.currency === "INR" ? "₹" : j.currency === "GBP" ? "£" : j.currency === "EUR" ? "€" : j.currency === "CAD" ? "C$" : j.currency === "AUD" ? "A$" : j.currency === "NZD" ? "NZ$" : j.currency === "JPY" ? "¥" : j.currency === "SAR" ? "SAR " : j.currency === "QAR" ? "QAR " : j.currency === "KWD" ? "KWD " : j.currency === "BHD" ? "BHD " : j.currency === "OMR" ? "OMR " : j.currency === "ZAR" ? "R" : j.currency === "MYR" ? "RM" : j.currency === "SGD" ? "S$" : "$"}{(j.salary_max || 0).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>

        {!paid && jobs.length >= 2 && <div className="mt-6"><UpgradeGate compact title="Want more job matches?" description="Free includes 2 matches. Upgrade to Pro or Premium for 6 matches and weekly refreshes." /></div>}
      </main>
    </div>
  );
}
